/* eslint-disable no-empty */
/* eslint-disable prefer-const */
import { isNumber, isString, isUndefined, repeat } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import { Format, getFormatFromLocale } from "./configuration";

const MAX_DIGITS = 22;
const DECIMAL_SEP = ".";
const ZERO_CHAR = "0";

type AbstractNumber = number | string;

const roundNumber = (parsedNumber, fractionSize, minFrac, maxFrac) => {
  const digits = parsedNumber.d;
  let fractionLen = digits.length - parsedNumber.i;

  // determine fractionSize if it is not specified; `+fractionSize` converts it to a number
  fractionSize = isUndefined(fractionSize)
    ? Math.min(Math.max(minFrac, fractionLen), maxFrac)
    : +fractionSize;

  // The index of the digit to where rounding is to occur
  const roundAt = fractionSize + parsedNumber.i;
  const digit = digits[roundAt];

  if (roundAt > 0) {
    // Drop fractional digits beyond `roundAt`
    digits.splice(Math.max(parsedNumber.i, roundAt));

    // Set non-fractional digits beyond `roundAt` to 0
    for (let j = roundAt; j < digits.length; j++) {
      digits[j] = 0;
    }
  } else {
    // We rounded to zero so reset the parsedNumber
    fractionLen = Math.max(0, fractionLen);
    parsedNumber.i = 1;
    digits[0] = 0;
    for (let i = 1; i < roundAt; i++) {
      digits[i] = 0;
    }
  }

  if (digit >= 5) {
    if (roundAt - 1 < 0) {
      for (let k = 0; k > roundAt; k--) {
        digits.unshift(0);
        parsedNumber.i++;
      }
      digits.unshift(1);
      parsedNumber.i++;
    } else {
      digits[roundAt - 1]++;
    }
  }

  // Pad out with zeros to get the required fraction length
  for (; fractionLen < Math.max(0, fractionSize); fractionLen++) {
    digits.push(0);
  }

  // Do any carrying, e.g. a digit was rounded up to 10
  const carry = digits.reduceRight((carry, d, i, digits) => {
    d = d + carry;
    digits[i] = d % 10;
    return Math.floor(d / 10);
  }, 0);
  if (carry) {
    digits.unshift(carry);
    parsedNumber.i++;
  }
};

const parse = (numStr) => {
  let exponent = 0,
    digits,
    numberOfIntegerDigits;
  let i, j, zeros;

  // Decimal point?
  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, "");
  }

  // Exponential form?
  if ((i = numStr.search(/e/i)) > 0) {
    // Work out the exponent.
    if (numberOfIntegerDigits < 0) {
      numberOfIntegerDigits = i;
    }
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    // There was no decimal point or exponent so it is an integer.
    numberOfIntegerDigits = numStr.length;
  }

  // Count the number of leading zeros.

  for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) {}

  if (i === (zeros = numStr.length)) {
    // The digits are all zero.
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    // Count the number of trailing zeros
    zeros--;
    while (numStr.charAt(zeros) === ZERO_CHAR) {
      zeros--;
    }

    // Trailing zeros are insignificant so ignore them
    numberOfIntegerDigits -= i;
    digits = [];
    // Convert string to array of digits without leading/trailing zeros.
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  // If the number overflows the maximum allowed digits then use an exponent.
  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return {
    d: digits,
    e: exponent,
    i: numberOfIntegerDigits,
  };
};

const formatNumber = (
  number: number,
  pattern: Format["PATTERNS"],
  groupSep: Format["GROUP_SEP"],
  decimalSep: Format["DECIMAL_SEP"],
  fractionSize: number | undefined
) => {
  if (!(isString(number) || isNumber(number)) || isNaN(number)) {
    return "";
  }

  const isInfinity = !isFinite(number);
  let isZero = false;
  let numStr = Math.abs(number) + "",
    formattedText: string,
    parsedNumber;

  if (isInfinity) {
    formattedText = "\u221e";
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

    let digits = parsedNumber.d;
    let integerLen = parsedNumber.i;
    const exponent = parsedNumber.e;
    let decimals: any[];
    isZero = digits.reduce((isZero, d) => isZero && !d, true);

    // pad zeros for small numbers
    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    // extract decimals digits
    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    // format the integer digits with grouping separators
    const groups = [];
    if (digits.length >= pattern.lgSize) {
      groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(""));
    }
    while (digits.length > pattern.gSize) {
      groups.unshift(digits.splice(-pattern.gSize, digits.length).join(""));
    }
    if (digits.length) {
      groups.unshift(digits.join(""));
    }
    formattedText = groups.join(groupSep);

    // append the decimal digits
    if (decimals.length) {
      formattedText += decimalSep + decimals.join("");
    }

    if (exponent) {
      formattedText += "e+" + exponent;
    }
  }
  if (number < 0 && !isZero) {
    return pattern.negPre + formattedText + pattern.negSuf;
  } else {
    return pattern.posPre + formattedText + pattern.posSuf;
  }
};

export const roundToDigits = (number: number, digits: number) => {
  if (digits < 0) {
    return Math.round(number * Math.pow(10, digits)) * Math.pow(10, -digits);
  } else {
    return Math.round(number * Math.pow(10, digits)) / Math.pow(10, digits);
  }
};

export function localize(locale: Locale, number: number): string {
  const { PATTERNS, GROUP_SEP, DECIMAL_SEP } = getFormatFromLocale(locale);
  if (number === null) {
    return "";
  } else {
    return formatNumber(number, PATTERNS, GROUP_SEP, DECIMAL_SEP, undefined);
  }
}

export function shorten(
  locale: Locale,
  number: number,
  precision: number = 3, // Number of significant figures to show
  signed: boolean = false,
  maxFloat: number = 4
): string {
  // Numeric base for calculations
  let base = 1000,
    // Compute sign
    sign = number >= 0 ? (signed ? "\u002B" : "") : "\u2212",
    // Other variable definitions
    suffixPosition: number,
    absVal: number,
    suffix: string,
    exponential: number;

  const { DECIMAL_MARK, SUFFIXES } = getFormatFromLocale(locale);

  // Edge values
  if (isNaN(number) || number === null) {
    return "N/A";
  } else if (number === 0 || isNaN(number) || Math.abs(number) === Infinity) {
    return number.toString();
  }

  // Compute absolute value
  absVal = Math.abs(number) || 0;

  // Very small values
  if (absVal < Math.pow(10, -maxFloat)) {
    return `<0${DECIMAL_MARK}${repeat("0", maxFloat - 1)}1`;
  }

  // Round to given max digits
  exponential = +absVal.toExponential().split("e")[1];

  absVal = roundToDigits(absVal, -exponential + precision - 1);

  // Round after given max decimals
  absVal = roundToDigits(absVal, maxFloat);

  // Compute suffix place
  suffixPosition = -1;
  while (absVal >= base) {
    absVal /= base;
    suffixPosition++;
  }
  suffix =
    suffixPosition >= 0 ? "\u202F" + (SUFFIXES[suffixPosition] || "?") : "";

  return sign + absVal.toString().replace(".", DECIMAL_MARK) + suffix;
}

export function colon(
  locale: Locale,
  label: string,
  number: AbstractNumber,
  short: boolean = true
): string {
  const { COLON_SEPARATOR } = getFormatFromLocale(locale);
  if (isString(number)) {
    return label + COLON_SEPARATOR + number;
  } else {
    return (
      label +
      COLON_SEPARATOR +
      (short ? shorten(locale, number) : localize(locale, number))
    );
  }
}
